'use client';

import { useEffect, useRef, useState } from 'react';
import type { TokenResult } from '@/lib/web3/types';

/* ---------- helpers ---------- */

const SIZE = 280;
const CX = SIZE / 2;
const CY = SIZE / 2;
const MAX_R = 95;
const N = 6;
const ANGLES = Array.from({ length: N }, (_, i) => i * (360 / N)); // 0, 60, 120, 180, 240, 300

function polar(angleDeg: number, r: number): [number, number] {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}

function polygonPoints(values: number[]): string {
  return values.map((v, i) => polar(ANGLES[i]!, (v / 100) * MAX_R).join(',')).join(' ');
}

function gridPoints(frac: number): string {
  return ANGLES.map((a) => polar(a, MAX_R * frac).join(',')).join(' ');
}

const GRID_FRACS = [0.25, 0.5, 0.75, 1];

/* ---------- score computations ---------- */

interface RadarValues {
  safety: number;
  organic: number;
  liquidity: number;
  community: number;
  supplyControl: number;
  authority: number;
}

interface AxisMeta {
  key: keyof RadarValues;
  label: string;
  desc: string;
}

const AXES: AxisMeta[] = [
  { key: 'safety',       label: 'Safety',        desc: 'Risk score (inverted)' },
  { key: 'organic',      label: 'Organic',        desc: 'Real trading vs bots' },
  { key: 'liquidity',    label: 'Liquidity',      desc: 'Market depth' },
  { key: 'community',    label: 'Community',      desc: 'Holder base size' },
  { key: 'supplyControl',label: 'Supply',         desc: 'No concentration risk' },
  { key: 'authority',    label: 'Authority',      desc: 'Mint & freeze status' },
];

function computeValues(result: TokenResult): RadarValues {
  const { score, intel, onchain, market } = result;

  const safety = 100 - score;

  const organic = intel?.organicScore != null ? Math.max(0, Math.min(100, intel.organicScore)) : 50;

  const liq = market?.liquidityUsd ?? 0;
  // log scale: $0 → 0, $1k → ~30, $10k → ~60, $100k → ~80, $1M → ~100
  const liquidity = liq <= 0 ? 0 : Math.min(100, Math.round((Math.log10(liq) / 6) * 100));

  const holders = intel?.holderCount ?? 0;
  // 0 → 0, 100 → 20, 500 → 50, 2000 → 80, 10000+ → 100
  const community = Math.min(100, Math.round((Math.log10(Math.max(1, holders)) / 4) * 100));

  const devPct = intel?.devBalancePct ?? 0;
  const topPct = intel?.topHoldersPct ?? 50;
  const supplyControl = Math.max(0, Math.round(100 - Math.max(devPct * 2.5, topPct * 0.8)));

  const mintOk = onchain?.available ? !onchain.mintAuthority : null;
  const freezeOk = onchain?.available ? !onchain.freezeAuthority : null;
  const authority =
    mintOk === null ? 60
    : mintOk && freezeOk ? 100
    : mintOk || freezeOk ? 55
    : 10;

  return { safety, organic, liquidity, community, supplyControl, authority };
}

/* ---------- verdict palette ---------- */

const PALETTE = {
  safe:    { stroke: '#10b981', fill: 'rgba(16,185,129,0.18)', glow: '#10b981', label: '#34d399' },
  caution: { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.18)', glow: '#f59e0b', label: '#fbbf24' },
  danger:  { stroke: '#f43f5e', fill: 'rgba(244,63,94,0.18)',  glow: '#f43f5e', label: '#fb7185' },
};

/* ---------- component ---------- */

export default function TokenRadar({ result }: { result: TokenResult }) {
  const [animated, setAnimated] = useState(false);
  const [hovered, setHovered] = useState<keyof RadarValues | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const [animScale, setAnimScale] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setAnimated(true);
      setAnimScale(1);
      return;
    }
    const DURATION = 900;
    function step(ts: number) {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / DURATION);
      // ease-out cubic
      const ease = 1 - Math.pow(1 - t, 3);
      setAnimScale(ease);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setAnimated(true);
      }
    }
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const values = computeValues(result);
  const palette = PALETTE[result.verdict];
  const valuesList = AXES.map((a) => values[a.key]);
  const animatedValues = valuesList.map((v) => v * animScale);

  const dataPoints = polygonPoints(animatedValues);
  const glowId = `radar-glow-${result.verdict}`;

  const hoveredAxis = hovered ? AXES.find((a) => a.key === hovered) : null;
  const hoveredVal = hovered ? Math.round(values[hovered]) : null;

  return (
    <div className="border border-white/10 bg-black/40 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">Security fingerprint</p>
          <p className="mt-0.5 text-sm text-neutral-300">
            Six-axis risk profile unique to this token
          </p>
        </div>
        {hoveredAxis && (
          <div className="text-right shrink-0">
            <p className="text-sm font-semibold text-white">{hoveredAxis.label}</p>
            <p className="text-xs text-neutral-500">{hoveredAxis.desc}</p>
            <p className={`text-xl font-bold font-mono mt-0.5`} style={{ color: palette.label }}>
              {hoveredVal}/100
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* SVG Radar */}
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full max-w-[260px] sm:max-w-[240px] shrink-0"
          aria-label="Token security fingerprint radar"
        >
          <defs>
            <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="label-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grid rings */}
          {GRID_FRACS.map((f) => (
            <polygon
              key={f}
              points={gridPoints(f)}
              fill="none"
              stroke={f === 1 ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)'}
              strokeWidth="1"
            />
          ))}

          {/* Grid level labels (25/50/75/100) */}
          {GRID_FRACS.map((f) => {
            const [x, y] = polar(0, MAX_R * f);
            return (
              <text
                key={`lbl-${f}`}
                x={x + 4}
                y={y - 3}
                fontSize="7"
                fill="rgba(255,255,255,0.2)"
                textAnchor="start"
              >
                {Math.round(f * 100)}
              </text>
            );
          })}

          {/* Axis spokes */}
          {ANGLES.map((a, i) => {
            const [x, y] = polar(a, MAX_R);
            return (
              <line
                key={i}
                x1={CX}
                y1={CY}
                x2={x}
                y2={y}
                stroke="rgba(255,255,255,0.07)"
                strokeWidth="1"
              />
            );
          })}

          {/* Data polygon fill */}
          <polygon
            points={dataPoints}
            fill={palette.fill}
            stroke="none"
          />

          {/* Data polygon border with glow */}
          <polygon
            points={dataPoints}
            fill="none"
            stroke={palette.stroke}
            strokeWidth="2"
            filter={animated ? `url(#${glowId})` : undefined}
            opacity={0.9}
          />

          {/* Axis dots + interactive hit areas */}
          {AXES.map((axis, i) => {
            const r = (values[axis.key] / 100) * MAX_R * animScale;
            const [dx, dy] = polar(ANGLES[i]!, r);
            const isHov = hovered === axis.key;
            return (
              <g key={axis.key}>
                <circle
                  cx={dx}
                  cy={dy}
                  r={isHov ? 5 : 3.5}
                  fill={isHov ? palette.stroke : 'rgba(0,0,0,0.8)'}
                  stroke={palette.stroke}
                  strokeWidth={isHov ? 2 : 1.5}
                  filter={isHov ? `url(#${glowId})` : undefined}
                  style={{ transition: 'r 0.15s, fill 0.15s' }}
                />
                {/* Invisible hit area */}
                <circle
                  cx={dx}
                  cy={dy}
                  r={12}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHovered(axis.key)}
                  onMouseLeave={() => setHovered(null)}
                  onTouchStart={() => setHovered(axis.key)}
                  onTouchEnd={() => setHovered(null)}
                />
              </g>
            );
          })}

          {/* Center dot */}
          <circle cx={CX} cy={CY} r={2.5} fill="rgba(255,255,255,0.2)" />

          {/* Axis labels */}
          {AXES.map((axis, i) => {
            const labelR = MAX_R * 1.18;
            const [lx, ly] = polar(ANGLES[i]!, labelR);
            const angle = ANGLES[i]!;
            const anchor =
              Math.abs(angle) < 10 || Math.abs(angle - 360) < 10
                ? 'middle'
                : angle < 180
                ? 'start'
                : angle === 180
                ? 'middle'
                : 'end';
            const isHov = hovered === axis.key;
            return (
              <text
                key={axis.key}
                x={lx}
                y={ly + (angle === 0 ? -3 : angle === 180 ? 9 : 4)}
                fontSize="9.5"
                fontWeight={isHov ? '700' : '500'}
                fill={isHov ? palette.label : 'rgba(255,255,255,0.55)'}
                textAnchor={anchor}
                filter={isHov ? `url(#label-glow)` : undefined}
                style={{ transition: 'fill 0.15s', userSelect: 'none' }}
              >
                {axis.label}
              </text>
            );
          })}
        </svg>

        {/* Score legend */}
        <div className="flex flex-col gap-2 w-full">
          {AXES.map((axis) => {
            const val = Math.round(values[axis.key]);
            const isHov = hovered === axis.key;
            const barColor =
              val >= 70 ? '#10b981'
              : val >= 40 ? '#f59e0b'
              : '#f43f5e';
            return (
              <div
                key={axis.key}
                className={`cursor-pointer rounded-none transition-all ${isHov ? 'opacity-100' : 'opacity-70 hover:opacity-90'}`}
                onMouseEnter={() => setHovered(axis.key)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px] font-medium text-neutral-300">{axis.label}</span>
                  <span className="text-[11px] font-mono tabular-nums" style={{ color: barColor }}>
                    {val}/100
                  </span>
                </div>
                <div className="h-1 w-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full transition-all duration-700"
                    style={{ width: `${val * animScale}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
          <p className="mt-2 text-[10px] text-neutral-600">
            Hover an axis to inspect. Scores are heuristic — always DYOR.
          </p>
        </div>
      </div>
    </div>
  );
}
